import { useDialogStore } from "@/components/alert-dialog-store";
import { Gender, type GuestDataProps } from "@/components/GuestForm";
import { environment } from "@/config/env";
import type { OcrResponse } from "@/types/OcrResponse";
import axios from "axios";

export const EU_COUNTRIES_ISO2 = [
  "AT",
  "BE",
  "BG",
  "HR",
  "CY",
  "CZ",
  "DK",
  "EE",
  "FI",
  "FR",
  "DE",
  "GR",
  "HU",
  "IE",
  "LV",
  "LT",
  "LU",
  "MT",
  "NL",
  "PL",
  "PT",
  "RO",
  "SK",
  "SI",
  "ES",
  "SE",
];

export function getAllowedDocumentTypes(countryIso2?: string): string[] {
  const iso = countryIso2?.trim().toUpperCase();
  if (!iso || iso === "IT") {
    return ["ID_CARD", "PASSPORT", "DRIVER_LICENSE"];
  }
  if (EU_COUNTRIES_ISO2.includes(iso)) {
    return ["ID_CARD", "PASSPORT"];
  }
  return ["PASSPORT"];
}

async function resolveItalianCityClientSide(
  cityName?: string | null,
  countryId: string | number = 107
): Promise<{ id: string; name: string } | null> {
  if (!cityName?.trim()) return null;
  try {
    const params = new URLSearchParams();
    params.append("search", cityName.trim());
    params.append("countryId", countryId.toString());
    const res = await fetch(`${environment.ELITE_BASE_URI}/geo/cities?${params.toString()}`);
    if (!res.ok) return null;
    const cities: Array<{ id: number | string; name: string }> = await res.json();
    if (!Array.isArray(cities) || cities.length === 0) return null;

    const lower = cityName.trim().toLowerCase();
    const exact = cities.find((c) => c.name?.toLowerCase() === lower);
    if (exact) {
      return { id: exact.id.toString(), name: exact.name };
    }
    // Se c'è solo un risultato o inizia esattamente
    const starts = cities.find((c) => c.name?.toLowerCase().startsWith(lower));
    if (starts) {
      return { id: starts.id.toString(), name: starts.name };
    }
  } catch (err) {
    console.warn("Error resolving Italian city client-side", err);
  }
  return null;
}

export function useDocumentOcr() {
  const openDialog = useDialogStore((state) => state.openDialog);

  const processDocument = async (
    file: File | undefined,
    onUpdate: (data: any) => void,
    onError?: (error: any) => void,
    backFile?: File,
  ) => {
    const formData = new FormData();
    if (file) formData.append("file", file);
    if (backFile) formData.append("backFile", backFile);

    try {
      const { data } = await axios.post<OcrResponse>(
        `${environment.ELITE_BASE_URI}/webcheckin/ocr`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );

      // Risoluzione asincrona di sicurezza per comuni italiani se mancava cityId
      if (data?.data) {
        const birth = data.data.identityData?.birth;
        const resObj =
          data.data.identityData?.residence && typeof data.data.identityData.residence === "object"
            ? data.data.identityData.residence
            : null;
        const docProps = data.data.documentProperties;

        const isBirthIt = !birth?.nation || birth.nation.trim().toUpperCase() === "IT";
        if (isBirthIt && birth?.city && (!birth.cityId || birth.cityId === "0")) {
          const matched = await resolveItalianCityClientSide(birth.city);
          if (matched) {
            birth.cityId = matched.id;
            birth.city = matched.name;
          } else {
            birth.city = "";
            birth.cityId = "";
          }
        }

        const isResIt = !resObj?.nation || resObj.nation.trim().toUpperCase() === "IT";
        if (isResIt && resObj?.city && (!resObj.cityId || resObj.cityId === "0")) {
          const matched = await resolveItalianCityClientSide(resObj.city);
          if (matched) {
            resObj.cityId = matched.id;
            resObj.city = matched.name;
          } else {
            resObj.city = "";
            resObj.cityId = "";
          }
        }

        const isIssuingIt = !docProps?.issuingCountry || docProps.issuingCountry.trim().toUpperCase() === "IT";
        if (isIssuingIt && docProps?.issuingCity && (!docProps.issuingCityId || docProps.issuingCityId === "0")) {
          const matched = await resolveItalianCityClientSide(docProps.issuingCity);
          if (matched) {
            docProps.issuingCityId = matched.id;
            docProps.issuingCity = matched.name;
          } else {
            docProps.issuingCity = "";
            docProps.issuingCityId = "";
          }
        }
      }

      onUpdate(data);
    } catch (err) {
      onError?.(file);
    }
  };

  const updateDocumentData = (
    data: GuestDataProps,
    response: OcrResponse,
    back: File,
    base64Back: string,
  ) => {
    const { identityData, documentProperties } = response.data || ({} as any);
    const birth = identityData?.birth;
    const resObj =
      identityData?.residence && typeof identityData.residence === "object"
        ? identityData.residence
        : null;

    const birthIso2 = birth?.nation?.trim().toUpperCase();
    const nationalityIso2 = identityData?.nationality?.trim().toUpperCase();
    const issuingIso2 = documentProperties?.issuingCountry
      ?.trim()
      .toUpperCase();
    const resIso2 = resObj?.nation?.trim().toUpperCase();
    const effectiveCountryIso2 = birthIso2 || nationalityIso2 || issuingIso2;
    const allowedTypes = getAllowedDocumentTypes(effectiveCountryIso2);

    const hasRecognizedFields = Boolean(
      response?.result !== false &&
        (documentProperties?.documentNumber?.trim() ||
          documentProperties?.expiryDate ||
          documentProperties?.mrz),
    );

    const rawMrz = documentProperties?.mrz as any;
    const mrzLine1 =
      typeof rawMrz === "string"
        ? rawMrz.trim()
        : typeof rawMrz?.line1 === "string"
          ? rawMrz.line1.trim()
          : "";

    let rawDocType = documentProperties?.type?.trim().toUpperCase() || "";
    if (rawDocType === "IDENTITY_CARD") rawDocType = "ID_CARD";

    // Se l'MRZ inizia con 'P' (es. P<USA..., P<ITA...), è sicuramente un passaporto
    if (mrzLine1.startsWith("P")) {
      rawDocType = "PASSPORT";
    }

    let resolvedDocType = "";
    if (hasRecognizedFields && rawDocType && allowedTypes.includes(rawDocType)) {
      resolvedDocType = rawDocType;
    }

    const normalizeIssuingAuthority = (raw?: string | null): string => {
      if (!raw?.trim()) return "";
      const upper = raw.trim().toUpperCase();
      if (upper.includes("COMUNE") || upper.includes("MUNICIP") || upper.includes("SINDACO")) return "Comune";
      if (upper.includes("MINISTERO") || upper.includes("INTERNO") || upper.includes("ESTERI") || upper.includes("MIT")) return "Ministero";
      if (upper.includes("QUESTURA") || upper.includes("QUESTORE")) return "Questura";
      if (upper.includes("MOTORIZZAZIONE") || upper.includes("MCTC") || upper.includes("UCO") || upper.includes("M.C.T.C")) return "Motorizzazione";
      if (upper.includes("PREFETTURA") || upper.includes("PREFETTO")) return "Prefettura";
      if (upper.includes("CONSOLATO") || upper.includes("AMBASCIATA") || upper.includes("CONSOLARE")) return "Consolato";
      if (upper.includes("POLIZIA") || upper.includes("COMMISSARIATO")) return "Polizia";
      if (upper.includes("CARABINIERI")) return "Carabinieri";
      const validOptions = ["Carabinieri", "Consolato", "Comune", "Ministero", "Motorizzazione", "Prefettura", "Polizia", "Questura", "Altro"];
      const matched = validOptions.find((o) => o.toUpperCase() === upper);
      return matched || "Altro";
    };

    const normalizedAuthority = normalizeIssuingAuthority(
      documentProperties?.issuingAuthority,
    );

    const birthProvCode = birth?.province?.trim().toUpperCase();
    const resProvCode = resObj?.province?.trim().toUpperCase();

    let isDocumentExpired = false;
    let parsedExpirationDate: Date | null = null;

    if (documentProperties?.expiryDate) {
      const rawDateStr = documentProperties.expiryDate.trim();
      let parsedDate: Date | null = null;

      const ddmmyyyyMatch = rawDateStr.match(/^(\d{1,2})[\/\.-](\d{1,2})[\/\.-](\d{4})$/);
      if (ddmmyyyyMatch) {
        const day = parseInt(ddmmyyyyMatch[1], 10);
        const month = parseInt(ddmmyyyyMatch[2], 10) - 1;
        const year = parseInt(ddmmyyyyMatch[3], 10);
        const d = new Date(year, month, day);
        if (!isNaN(d.getTime())) {
          parsedDate = d;
        }
      } else {
        const d = new Date(rawDateStr);
        if (!isNaN(d.getTime())) {
          parsedDate = d;
        }
      }

      if (parsedDate) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const expCheck = new Date(parsedDate);
        expCheck.setHours(0, 0, 0, 0);

        if (expCheck < today) {
          isDocumentExpired = true;
          parsedExpirationDate = null;
        } else {
          parsedExpirationDate = parsedDate;
        }
      }
    }

    const resolveCityField = (
      isItaly: boolean,
      cityId?: string | null,
      cityName?: string | null,
      existingCity?: { id: string | number; name: string } | null
    ): { id: string; name: string } | undefined => {
      const name = cityName?.trim();
      const id = cityId?.trim();
      if (isItaly) {
        if (id && id !== "0" && name) {
          return { id, name };
        }
        return existingCity ? { id: existingCity.id.toString(), name: existingCity.name } : undefined;
      }
      return name ? { id: id || "0", name } : (existingCity ? { id: existingCity.id.toString(), name: existingCity.name } : undefined);
    };

    const isIssuingIt = !issuingIso2 || issuingIso2 === "IT";
    const resolvedIssuingCity = resolveCityField(
      isIssuingIt,
      documentProperties?.issuingCityId,
      documentProperties?.issuingCity,
      data.issuingCity
    );

    const isBirthIt = !birthIso2 || birthIso2 === "IT";
    const resolvedBirthCity = resolveCityField(
      isBirthIt,
      birth?.cityId,
      birth?.city,
      data.birthCity
    );

    const isResIt = !resIso2 || resIso2 === "IT";
    const resolvedResidenceCity = resolveCityField(
      isResIt,
      resObj?.cityId,
      resObj?.city,
      data.residenceCity
    );

    const updatedData: GuestDataProps = {
      ...data,
      documentType: resolvedDocType,
      documentNumber: documentProperties?.documentNumber || data.documentNumber,
      issuingDate: documentProperties?.issueDate
        ? new Date(documentProperties.issueDate)
        : data.issuingDate ?? null,
      expirationDate: isDocumentExpired
        ? null
        : (parsedExpirationDate ?? data.expirationDate ?? null),
      isDocumentExpired,
      issuingCountry: issuingIso2
        ? {
            id: 0,
            name:
              issuingIso2 === "IT"
                ? "Italia"
                : documentProperties?.issuingCountry || issuingIso2,
            iso2: issuingIso2,
          }
        : data.issuingCountry,
      issuingAuthority: normalizedAuthority || data.issuingAuthority,
      issuingCity: resolvedIssuingCity,
      name: identityData?.firstName || data.name,
      surname: identityData?.lastName || data.surname,
      birthDate: birth?.date ? new Date(birth.date) : data.birthDate,
      birthCountry: birthIso2
        ? {
            id: 0,
            name:
              birthIso2 === "IT"
                ? "Italia"
                : birth?.nation || birthIso2,
            iso2: birthIso2,
          }
        : data.birthCountry,
      birthProvince: birthProvCode
        ? {
            code: birthProvCode,
            name: birthProvCode,
          }
        : data.birthProvince,
      birthCity: resolvedBirthCity,
      residenceCountry: resIso2
        ? {
            id: 0,
            name:
              resIso2 === "IT"
                ? "Italia"
                : resObj?.nation || resIso2,
            iso2: resIso2,
          }
        : data.residenceCountry,
      residenceProvince: resProvCode
        ? {
            code: resProvCode,
            name: resProvCode,
          }
        : data.residenceProvince,
      residenceCity: resolvedResidenceCity,
      residenceAddress: resObj?.address?.trim() || data.residenceAddress,
      residenceStreetNumber:
        resObj?.streetNumber?.trim() || data.residenceStreetNumber,
      cap: resObj?.postalCode?.trim() || data.cap,
      nationality: identityData?.nationality || data.nationality,
      gender:
        identityData?.sex === "M"
          ? Gender.Male
          : identityData?.sex === "F"
            ? Gender.Female
            : data.gender,
      taxCode: identityData?.taxCode ?? data.taxCode ?? undefined,
      backDocumentData: back,
      base64BackData: base64Back,
      ocrResponse: response.data,
    };
    return updatedData;
  };

  return { processDocument, updateDocumentData };
}
